import { createDictionary, uniq } from 'utils/ObjectUtils';

import { EXTENSIONS_ENUM } from './ExtensionConstants';
import { INTERVENTION_MODE } from './InterventionConstants';

export const ADMIN = 'ADMIN';
export const AUDITOR = 'AUDITOR';
export const SALESMAN = 'SALESMAN';
export const CUSTOMER = 'CUSTOMER';
export const READ_ONLY = 'READ_ONLY';
export const SCHEDULER = 'SCHEDULER';
export const TECHNICIAN = 'TECHNICIAN';
export const INTEGRATION = 'INTEGRATION';
export const BACK_OFFICE = 'BACK_OFFICE';
export const MANUFACTURER = 'MANUFACTURER';
export const GROUP_MANAGER = 'GROUP_MANAGER';
export const AUDIT_MANAGER = 'AUDIT_MANAGER';
export const RELEASE_MANAGER = 'RELEASE_MANAGER';
export const VO_STOCK_MANAGER = 'VO_STOCK_MANAGER';
export const KEY_DEPOSIT_MANAGER = 'KEY_DEPOSIT_MANAGER';
export const NO_CONFIGURATION_ACCESS = 'NO_CONFIGURATION_ACCESS';

export const SALES_MANAGER = 'SALES_MANAGER';
export const AFTER_SALES_MANAGER = 'AFTER_SALES_MANAGER';
export const PARTS_MANAGER = 'PARTS_MANAGER';
export const PARTS_OFFICER = 'PARTS_OFFICER';
export const CAMPAIGN_MANAGER = 'CAMPAIGN_MANAGER';
export const CAMPAIGN_OFFICER = 'CAMPAIGN_OFFICER';

export const SELF_SERVICE_MANAGER = 'SELF_SERVICE_MANAGER';
export const SELF_SERVICE_OFFICER = 'SELF_SERVICE_OFFICER';

export const SCHEDULABLE_EXPORT_TIME = {
  WEEKLY: 'WEEKLY',
  MONTHLY: 'MONTHLY'
};

// ⚠ Order is important for other definitions
export const ALL_ROLES = [
  ADMIN,
  MANUFACTURER,
  GROUP_MANAGER,
  BACK_OFFICE,
  AFTER_SALES_MANAGER,
  TECHNICIAN,
  SALES_MANAGER,
  SALESMAN,
  VO_STOCK_MANAGER,
  AUDIT_MANAGER,
  AUDITOR,
  KEY_DEPOSIT_MANAGER,
  CUSTOMER,
  READ_ONLY,
  SCHEDULER,
  NO_CONFIGURATION_ACCESS,
  PARTS_MANAGER,
  PARTS_OFFICER,
  CAMPAIGN_MANAGER,
  CAMPAIGN_OFFICER,
  SELF_SERVICE_MANAGER,
  SELF_SERVICE_OFFICER
];

export const ALL_ROLES_WITHOUT_MANUFACTURER = ALL_ROLES.filter(role => role !== MANUFACTURER);

export const ROLES_WITH_NOTIFICATIONS = ALL_ROLES.filter(role => role !== ADMIN && role !== MANUFACTURER);

export const CHAT_ROLES = ALL_ROLES.filter(role =>
  ![MANUFACTURER, AUDIT_MANAGER, AUDITOR, KEY_DEPOSIT_MANAGER].includes(role));

export const LEADERSHIP_ROLES = [ADMIN, GROUP_MANAGER];

export const MANAGER_ROLES = [SALES_MANAGER, AUDIT_MANAGER, AFTER_SALES_MANAGER, KEY_DEPOSIT_MANAGER, PARTS_MANAGER, CAMPAIGN_MANAGER, SELF_SERVICE_MANAGER];

export const LEADERS_AND_MANAGERS = [...LEADERSHIP_ROLES, ...MANAGER_ROLES];

export const USER_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE'
};

export const USER_TYPE = {
  DEVICE: 'DEVICE',
  SYSTEM: 'SYSTEM',
  USER: 'USER'
};

export const USER_GROUPS_ALLOWED_ROLES = [
  AUDITOR,
  SALESMAN,
  TECHNICIAN,
  BACK_OFFICE,
  AUDIT_MANAGER,
  VO_STOCK_MANAGER,
  KEY_DEPOSIT_MANAGER,
  SALES_MANAGER,
  AFTER_SALES_MANAGER,
  PARTS_MANAGER,
  CAMPAIGN_MANAGER,
  SELF_SERVICE_MANAGER,
  SELF_SERVICE_OFFICER
];

export const EMAIL_STATUS = {
  VALID: 'VALID',
  INVALID: 'INVALID',
  TRANSIENT: 'TRANSIENT'
};

export const ROLES_BY_INTERVENTION_MODE = createDictionary({
  [INTERVENTION_MODE.TECHNICIAN]: [AFTER_SALES_MANAGER, BACK_OFFICE, TECHNICIAN, SELF_SERVICE_MANAGER, SELF_SERVICE_OFFICER],
  [INTERVENTION_MODE.SALES]: [SALES_MANAGER, VO_STOCK_MANAGER, BACK_OFFICE, SALESMAN],
  [INTERVENTION_MODE.PARTS]: [PARTS_MANAGER, PARTS_OFFICER, BACK_OFFICE, SALESMAN]
}, USER_GROUPS_ALLOWED_ROLES);

export const ROLES_BY_EXTENSIONS = {
  [EXTENSIONS_ENUM.AFTER_SALES]: [AFTER_SALES_MANAGER, TECHNICIAN, BACK_OFFICE],
  [EXTENSIONS_ENUM.SALES]: [SALES_MANAGER, SALESMAN, VO_STOCK_MANAGER, BACK_OFFICE],
  [EXTENSIONS_ENUM.PARTS]: [PARTS_MANAGER, PARTS_OFFICER, BACK_OFFICE],
  [EXTENSIONS_ENUM.AUDIT]: [AUDIT_MANAGER, AUDITOR, BACK_OFFICE],
  [EXTENSIONS_ENUM.APPOINTMENT]: [KEY_DEPOSIT_MANAGER, BACK_OFFICE],
  [EXTENSIONS_ENUM.CAMPAIGN]: [CAMPAIGN_MANAGER, CAMPAIGN_OFFICER],
  [EXTENSIONS_ENUM.SELF_SERVICE]: [SELF_SERVICE_MANAGER, SELF_SERVICE_OFFICER]
};

export const INTERVENTION_ROLES_WO_SELF_SERVICE = [
  ...LEADERSHIP_ROLES,
  ...Object.values(ROLES_BY_INTERVENTION_MODE)
    .flatMap((role: any) => role)
    .filter((role: any) => !ROLES_BY_EXTENSIONS.SELF_SERVICE.includes(role))
];

export const ALL_EXTENSIONS_ROLES = uniq(Object.values(ROLES_BY_EXTENSIONS).flatMap((role: any) => role));

export const getExtensionsByRoles = (roles: any[]) =>
  Object.entries(ROLES_BY_EXTENSIONS).reduce((acc: any[], [extension, extensionRoles]) => {
    const containsRole = roles.some((role: any) => extensionRoles.includes(role));
    return containsRole ? [...acc, extension] : acc;
  }, []);

export const USER_WARNINGS = {
  DELETED_PERSONAL_DEVICES: 'deletePersonalDevices',
  DELETED_ROLES: 'deletedRoles',
  INACTIVE: 'inactive'
};
